import { apiUrl } from "@/lib/constants";
import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

const TELEGRAM_INIT_DATA_WAIT_MS = 4_000;
const TELEGRAM_INIT_DATA_POLL_MS = 100;

export async function establishMiniAppAuthSession(
  initData: string,
  opts: { vpsId?: string } = {},
): Promise<
  | { ok: true }
  | { ok: false; state: "unlinked" | "error"; message: string }
> {
  try {
    const res = await fetch(apiUrl("/auth/telegram/miniapp/session"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        init_data: initData,
        ...(opts.vpsId?.trim() ? { vps_id: opts.vpsId.trim() } : {}),
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (res.ok) {
      return { ok: true };
    }
    if (body && typeof body === "object" && (body as { error?: string }).error === "telegram_not_linked") {
      return {
        ok: false,
        state: "unlinked",
        message: "This Telegram account is not linked to a chatcode account yet.",
      };
    }
    if (
      body &&
      typeof body === "object" &&
      (body as { error?: string }).error === "telegram_miniapp_auth_date_expired"
    ) {
      return {
        ok: false,
        state: "error",
        message: "Telegram session expired. Reopen this Mini App from the bot.",
      };
    }
    return {
      ok: false,
      state: "error",
      message: "Failed to authorize this Mini App session.",
    };
  } catch {
    return {
      ok: false,
      state: "error",
      message: "Failed to authorize this Mini App session.",
    };
  }
}

export async function maybeRetryMiniAppAuth(
  authStatus: "loading" | "unauthenticated" | "authenticated",
  telegramInitData: string,
  setAuthAttempt: Dispatch<SetStateAction<number>>,
): Promise<void> {
  if (authStatus === "loading") return;
  if (!telegramInitData) return;
  const authStillValid = await fetch(apiUrl("/auth/me"), {
    credentials: "include",
  })
    .then((res) => res.status !== 401)
    .catch(() => true);
  if (authStillValid) return;
  setAuthAttempt((value) => value + 1);
}

export function getTelegramWebAppInitData(): string {
  if (typeof window === "undefined") {
    return "";
  }
  const value = window.Telegram?.WebApp?.initData;
  return typeof value === "string" ? value.trim() : "";
}

export function useTelegramWebAppInitData(): {
  initData: string;
  waiting: boolean;
} {
  const [initData, setInitData] = useState(() => getTelegramWebAppInitData());
  const [waiting, setWaiting] = useState(() => !initData);

  useEffect(() => {
    if (initData) {
      setWaiting(false);
      return;
    }

    let stopped = false;
    const deadline = Date.now() + TELEGRAM_INIT_DATA_WAIT_MS;
    const read = () => {
      if (stopped) return true;
      const next = getTelegramWebAppInitData();
      if (next) {
        setInitData(next);
        setWaiting(false);
        return true;
      }
      if (Date.now() >= deadline) {
        setWaiting(false);
        return true;
      }
      return false;
    };

    if (read()) return;

    const timer = window.setInterval(() => {
      if (read()) {
        window.clearInterval(timer);
      }
    }, TELEGRAM_INIT_DATA_POLL_MS);
    const handleVisibilityOrFocus = () => {
      if (read()) {
        window.clearInterval(timer);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, [initData]);

  return { initData, waiting };
}

export function getTelegramWebAppStartParam(): string {
  if (typeof window === "undefined") {
    return "";
  }
  const unsafe = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (typeof unsafe === "string" && unsafe.trim()) {
    return unsafe.trim();
  }
  const params = new URLSearchParams(window.location.search);
  return params.get("tgWebAppStartParam")?.trim() || "";
}

export function useTelegramMiniAppNoZoom(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;

    const viewport = document.querySelector<HTMLMetaElement>("meta[name='viewport']");
    const previousViewport = viewport?.getAttribute("content") ?? null;
    viewport?.setAttribute(
      "content",
      "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover",
    );

    const preventGesture = (event: Event) => {
      event.preventDefault();
    };
    const preventMultitouch = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    document.addEventListener("gesturestart", preventGesture, { passive: false });
    document.addEventListener("gesturechange", preventGesture, { passive: false });
    document.addEventListener("touchmove", preventMultitouch, { passive: false });

    return () => {
      if (viewport) {
        if (previousViewport === null) {
          viewport.removeAttribute("content");
        } else {
          viewport.setAttribute("content", previousViewport);
        }
      }
      document.removeEventListener("gesturestart", preventGesture);
      document.removeEventListener("gesturechange", preventGesture);
      document.removeEventListener("touchmove", preventMultitouch);
    };
  }, [enabled]);
}

export function formatMiniAppWorkdir(workdir: string): string {
  const trimmed = workdir.trim();
  if (!trimmed || trimmed === ".") {
    return "~/workspace";
  }
  if (trimmed.startsWith("~/")) {
    return trimmed;
  }
  if (trimmed.startsWith("/home/")) {
    return trimmed.replace(/^\/home\/[^/]+/, "~");
  }
  const normalized = trimmed.replace(/^\.\//, "").replace(/^\/+/, "");
  if (normalized.startsWith("workspace/")) {
    return `~/${normalized}`;
  }
  return `~/workspace/${normalized}`;
}

export function showTelegramStarterUpgradePopup(input: {
  title: string;
  message: string;
  createInvoiceLink: () => Promise<{ url: string } | string>;
  plansUrl?: string;
  onPaid?: () => void | Promise<void>;
}): boolean {
  const webApp = window.Telegram?.WebApp;
  if (typeof webApp?.showPopup !== "function") {
    return false;
  }
  const plansUrl = input.plansUrl || `${window.location.origin}/settings/plan`;
  webApp.showPopup(
    {
      title: input.title,
      message: input.message,
      buttons: [
        { id: "buy_starter", type: "default", text: "⭐️ Buy Starter" },
        { id: "all_plans", type: "default", text: "All Plans" },
        { id: "cancel", type: "cancel", text: "Cancel" },
      ],
    },
    (buttonId) => {
      if (buttonId === "buy_starter") {
        void openTelegramStarterInvoice(input.createInvoiceLink, input.onPaid);
        return;
      }
      if (buttonId === "all_plans") {
        openTelegramExternalLink(plansUrl);
      }
    },
  );
  return true;
}

export function showTelegramPlansUpgradePopup(input: {
  title: string;
  message: string;
  plansUrl?: string;
}): boolean {
  const webApp = window.Telegram?.WebApp;
  if (typeof webApp?.showPopup !== "function") {
    return false;
  }
  const plansUrl = input.plansUrl || `${window.location.origin}/settings/plan`;
  webApp.showPopup(
    {
      title: input.title,
      message: input.message,
      buttons: [
        { id: "upgrade", type: "default", text: "Upgrade" },
        { id: "cancel", type: "cancel", text: "Cancel" },
      ],
    },
    (buttonId) => {
      if (buttonId === "upgrade" || buttonId === "ok") {
        openTelegramExternalLink(plansUrl);
      }
    },
  );
  return true;
}

export async function openTelegramStarterInvoice(
  createInvoiceLink: () => Promise<{ url: string } | string>,
  onPaid?: () => void | Promise<void>,
): Promise<void> {
  const webApp = window.Telegram?.WebApp;
  try {
    const result = await createInvoiceLink();
    const url = typeof result === "string" ? result : result.url;
    if (typeof webApp?.openInvoice === "function") {
      webApp.openInvoice(url, (status) => {
        if (status === "paid" && onPaid) {
          void Promise.resolve(onPaid());
        }
      });
      return;
    }
    if (typeof webApp?.openTelegramLink === "function") {
      webApp.openTelegramLink(url);
      return;
    }
    webApp?.showAlert?.("Starter checkout must be opened inside Telegram.");
  } catch {
    webApp?.showAlert?.("Starter checkout could not be opened.");
  }
}

function openTelegramExternalLink(url: string): void {
  const webApp = window.Telegram?.WebApp;
  if (typeof webApp?.openLink === "function") {
    webApp.openLink(url, { try_instant_view: false });
    return;
  }
  window.location.href = url;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        initDataUnsafe?: {
          start_param?: string;
        };
        isVerticalSwipesEnabled?: boolean;
        ready?(): void;
        expand?(): void;
        enableVerticalSwipes?(): void;
        disableVerticalSwipes?(): void;
        hideKeyboard?(): void;
        openLink?(url: string, opts?: { try_instant_view?: boolean }): void;
        openTelegramLink?(url: string): void;
        openInvoice?(
          url: string,
          callback?: (status: "paid" | "cancelled" | "failed" | "pending") => void,
        ): void;
        downloadFile?(
          params: { url: string; file_name: string },
          callback?: (accepted: boolean) => void,
        ): void;
        HapticFeedback?: {
          impactOccurred?(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
          notificationOccurred?(type: "error" | "success" | "warning"): void;
          selectionChanged?(): void;
        };
        showAlert?(message: string, callback?: () => void): void;
        showPopup?(
          params: {
            title?: string;
            message: string;
            buttons?: Array<{
              id?: string;
              type?: "default" | "ok" | "close" | "cancel" | "destructive";
              text?: string;
            }>;
          },
          callback?: (buttonId?: string) => void,
        ): void;
        setBackgroundColor?(color: string): void;
        setBottomBarColor?(color: string): void;
        onEvent?(eventType: string, handler: (event?: unknown) => void): void;
        offEvent?(eventType: string, handler: (event?: unknown) => void): void;
        MainButton?: {
          setText?(text: string): void;
          onClick?(handler: () => void): void;
          offClick?(handler: () => void): void;
          show?(): void;
          hide?(): void;
          enable?(): void;
          disable?(): void;
          showProgress?(leaveActive?: boolean): void;
          hideProgress?(): void;
          setParams?(params: {
            text?: string;
            color?: string;
            text_color?: string;
            is_active?: boolean;
            is_visible?: boolean;
            has_shine_effect?: boolean;
          }): void;
        };
        BackButton?: {
          show?(): void;
          hide?(): void;
          onClick?(handler: () => void): void;
          offClick?(handler: () => void): void;
        };
      };
    };
  }
}
